import { useEffect } from "react";

/**
 * Lightweight SEO helper without extra dependencies.
 * Sets <title>, meta description, canonical, OG tags, and injects optional JSON-LD.
 */
export function useSEO({ title, description, canonical, jsonLd, keywords, image, robots }) {
  useEffect(() => {
    if (title) document.title = title;

    const upsertMeta = (selector, attrs) => {
      let tag = document.head.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        Object.entries(attrs.create || {}).forEach(([k, v]) => tag.setAttribute(k, v));
        document.head.appendChild(tag);
      }
      Object.entries(attrs.set || {}).forEach(([k, v]) => tag.setAttribute(k, v));
    };

    if (description) {
      upsertMeta('meta[name="description"]', {
        create: { name: "description" },
        set: { content: description },
      });
      upsertMeta('meta[property="og:description"]', {
        create: { property: "og:description" },
        set: { content: description },
      });
      upsertMeta('meta[name="twitter:description"]', {
        create: { name: "twitter:description" },
        set: { content: description },
      });
    }

    if (title) {
      upsertMeta('meta[property="og:title"]', {
        create: { property: "og:title" },
        set: { content: title },
      });
      upsertMeta('meta[name="twitter:title"]', {
        create: { name: "twitter:title" },
        set: { content: title },
      });
    }

    if (keywords) {
      upsertMeta('meta[name="keywords"]', {
        create: { name: "keywords" },
        set: { content: keywords },
      });
    }

    upsertMeta('meta[name="robots"]', {
      create: { name: "robots" },
      set: { content: robots || "index, follow, max-image-preview:large" },
    });

    if (image) {
      upsertMeta('meta[property="og:image"]', {
        create: { property: "og:image" },
        set: { content: image },
      });
      upsertMeta('meta[name="twitter:image"]', {
        create: { name: "twitter:image" },
        set: { content: image },
      });
    }

    if (canonical) {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement("link");
        link.setAttribute("rel", "canonical");
        document.head.appendChild(link);
      }
      link.setAttribute("href", canonical);

      upsertMeta('meta[property="og:url"]', {
        create: { property: "og:url" },
        set: { content: canonical },
      });
    }

    // JSON-LD
    const existing = document.head.querySelectorAll('script[data-sloto-jsonld="true"]');
    existing.forEach((el) => el.remove());
    if (jsonLd) {
      const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      blocks.forEach((block) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.dataset.slotoJsonld = "true";
        script.text = JSON.stringify(block);
        document.head.appendChild(script);
      });
    }

    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, JSON.stringify(jsonLd), keywords, image, robots]);
}

export const breadcrumb = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: it.url,
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const imageObject = (url, name = "Sloto69") => ({
  "@type": "ImageObject",
  url,
  name,
});

export const organizationSchema = ({ site, sameAs = [], logo }) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: site.name,
  alternateName: "Sloto69 Indonesia",
  url: site.baseUrl,
  logo: imageObject(`${site.baseUrl}${logo}`, `${site.name} Logo`),
  sameAs,
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      telephone: `+${site.whatsappNumber}`,
      areaServed: "ID",
      availableLanguage: ["id-ID"],
    },
  ],
});

export const websiteSchema = ({ site }) => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: site.name,
  url: site.baseUrl,
  inLanguage: "id-ID",
  potentialAction: {
    "@type": "SearchAction",
    target: `${site.baseUrl}/artikel?q={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
});

export const articleSchema = ({ article, site, logo, image }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: article.title,
  description: article.excerpt,
  datePublished: article.publishedAt,
  dateModified: article.dateModified || article.updatedAt || article.publishedAt,
  inLanguage: "id-ID",
  keywords: article.keywords,
  author: {
    "@type": "Organization",
    name: site.name,
    url: site.baseUrl,
  },
  publisher: {
    "@type": "Organization",
    name: site.name,
    logo: imageObject(`${site.baseUrl}${logo}`, `${site.name} Logo`),
  },
  image: imageObject(image, article.title),
  mainEntityOfPage: `${site.baseUrl}/artikel/${article.slug}`,
  articleSection: article.category,
});
